// Gráfico da curva de desembolso Ampli DENTRO da tela de Desembolso.
//
// A FONTE (design D15) é a MESMA que alimenta a raiz da tabela da EAP: a soma, por
// competência, do desembolso de cada unidade (`project.units[].monthlyProjection
// .ampliDisbursement`, o que `getUnits` publica), recortada pelo MESMO período
// (`filterDates`, o do `DisbursementPeriodFilter`). As unidades são recarregadas quando o
// período muda, então barra e raiz da EAP são o mesmo número em qualquer janela — inclusive
// uma janela MAIOR que a da seleção da obra.
//
// O que foi corrigido: o gráfico lia `plannedCashFlowSeries.ampliDisbursement`, que o
// `getDetail` recorta pela janela da seleção da obra (`selectProject`) e que a troca de
// período não recarrega. Preso à janela antiga (07/2026–01/2027 no caso visto em stg), ele
// mostrava menos da curva do que a EAP (desde 05/2024). Essa série continua como plano B só
// ENQUANTO as unidades não chegaram — a primeira pintura nunca fica em branco — e nunca
// `resolveAmpliSeries`/`project.ampliDisbursements`, o recorte da Projeção Financeira que
// sobrevive à troca de tela.
//
// Revisão de 2026-10-13 (design D4): junto do desembolso resultante (barras), duas séries de
// comparação em linha, ambas servidas pelo back-end — nada é recalculado aqui:
//
// - o Previsto da obra inteira: `physicals`, a mesma série da Projeção Financeira
//   (recarregada pelo `getFollowUps` a cada troca de período), com a fonte em
//   `physicalsSource` ("prevision" | "erp");
// - o desembolso inicial: `plannedCashFlowSeries.ampliDisbursementOrigin`, a origem da raiz
//   antes das regras, publicada inteira e recortada aqui pelo período.
//
// Quando a origem da curva é o próprio Previsto (mesma fonte), as duas coincidem e
// viram uma série só (`physicals`), com legenda que diz isso. Uma série que a obra não tem é omitida, com o
// motivo abaixo do gráfico — nunca desenhada como zero.
import { useEffect, useMemo } from "react";
import { useProjectActions, useProjectContext } from "@contexts/projects";

import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
  isAmpliDisbursementPolicy,
} from "../financialProjection/cashFlowPlanning";
import MonthlyBarChart from "./MonthlyBarChart";
import {
  PHYSICAL_SOURCE_LABELS,
  PHYSICAL_SOURCE_OF_ORIGIN,
  UNAVAILABLE_HINT,
  normalizeOriginState,
  isMegaProject,
  originMatchesPhysicalSource,
} from "./originChain";
import { INITIAL_COLOR, PHYSICAL_COLOR, RESULTING_COLOR } from "./chartColors";
import monthsBetween from "./months";

const STATE_DETAILS = Object.freeze({
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  unavailable: UNAVAILABLE_HINT,
});

/** `{ month, year, value }` → `"MM/AAAA"`, a competência no formato das colunas da EAP. */
const competenceOf = (point) =>
  `${String(point.month).padStart(2, "0")}/${point.year}`;

/** `YYYYMM` de `"MM/AAAA"`, só para ordenar. */
const sortKey = (date) => String(date).slice(3) + String(date).slice(0, 2);

const bySortKey = (left, right) =>
  sortKey(left.date) < sortKey(right.date) ? -1 : 1;

/**
 * A fonte da raiz da EAP: o desembolso de cada unidade somado por competência. `null`
 * quando as unidades ainda não chegaram (ausente é diferente de "sem desembolso").
 */
const rootSeriesFromUnits = (units) => {
  if (!Array.isArray(units)) return null;
  const byMonth = new Map();
  units.forEach((unit) => {
    const points = unit?.monthlyProjection?.ampliDisbursement;
    if (!Array.isArray(points)) return;
    points.forEach((point) => {
      const date = competenceOf(point);
      byMonth.set(date, (byMonth.get(date) ?? 0) + (Number(point.value) || 0));
    });
  });
  return [...byMonth.entries()].map(([date, value]) => ({ date, value }));
};

const publishedSeries = (series) => (Array.isArray(series) ? series : []);

/**
 * Uma barra por mês do período escolhido, na ordem do período — não na ordem em que a
 * série chegou. Um mês sem valor na curva entra como zero: a lacuna no meio de um
 * período contínuo é informação (naquele mês não se desembolsa nada), sumir com a
 * barra encostaria meses não vizinhos e mentiria sobre o intervalo.
 *
 * Sem período legível, a série inteira, ordenada — é o que a tela mostrava antes de
 * existir seletor, e continua sendo o melhor palpite.
 */
const alignToPeriod = (series, months) => {
  if (months.length === 0) return [...series].sort(bySortKey);
  const byMonth = new Map(series.map((point) => [point.date, point]));
  return months.map((date) => ({ date, value: byMonth.get(date)?.value ?? 0 }));
};

const seriesForPeriod = (project, months) => {
  const fromUnits = rootSeriesFromUnits(project?.units);
  const resulting =
    fromUnits ?? publishedSeries(project?.plannedCashFlowSeries?.ampliDisbursement);
  return alignToPeriod(resulting, months);
};

export const RESULTING_LABEL = "Desembolso resultante";
export const NO_PHYSICAL_TEXT =
  "Esta obra não tem Previsto (nem do Prevision nem do ERP): a série não é desenhada.";
// Obra MEGA: previsto do Prevision quando há integração, senão o previsto mensal do MEGA.
export const NO_PREVISTO_TEXT =
  "Esta obra não tem previsto (nem do Prevision nem o mensal do MEGA): a série não é desenhada.";
export const NO_INITIAL_TEXT =
  "O desembolso inicial (a origem antes das regras) não está disponível nesta curva.";


const lineDataset = (label, color, points) => ({
  type: "line",
  label,
  data: points.map((point) => Number(point.value) || 0),
  borderColor: color,
  backgroundColor: color,
  borderWidth: 2,
  pointRadius: 2,
  fill: false,
  // Desenhada por cima das barras.
  order: 0,
});

/**
 * As séries de comparação do topo, já alinhadas ao período: `{ datasets, notes }`. `notes`
 * são os motivos das séries omitidas.
 */
export const comparisonSeries = (project, months) => {
  const notes = [];
  const datasets = [];
  const source = project?.physicalsSource;
  const physicals = Array.isArray(project?.physicals) ? project.physicals : [];
  // `undefined` é payload anterior ao campo: com série, ela é desenhada sem a fonte.
  const physicalAvailable = source === undefined
    ? physicals.length > 0
    : Boolean(PHYSICAL_SOURCE_LABELS[source]);
  // O nome vem pronto do back-end (`physicalsLabel`, que conhece o ERP da obra).
  const physicalLabel = project?.physicalsLabel ?? PHYSICAL_SOURCE_LABELS[source]
    ?? "Previsto";
  const initial = project?.plannedCashFlowSeries?.ampliDisbursementOrigin;
  const initialAvailable = Array.isArray(initial) && initial.length > 0;
  const origin = normalizeOriginState(project?.ampliDisbursement?.origin);
  const merged = physicalAvailable
    && origin !== null
    && PHYSICAL_SOURCE_OF_ORIGIN[origin.kind] !== undefined
    && (source === undefined || originMatchesPhysicalSource(origin.kind, source));

  if (merged) {
    // Revisão de 2026-10-15: a linha é SEMPRE o Previsto da obra (`physicals`, a soma por
    // serviço que a Projeção Financeira também desenha). Já foi `ampliDisbursementOrigin`
    // aqui, e as duas telas mostravam meses diferentes sob o mesmo nome. Origem e fonte
    // coincidem, então só a legenda diz que a linha é também o desembolso inicial.
    datasets.push(lineDataset(
      `${physicalLabel} / desembolso inicial`,
      PHYSICAL_COLOR,
      alignToPeriod(physicals, months),
    ));
    return { datasets, notes };
  }
  if (physicalAvailable) {
    datasets.push(lineDataset(physicalLabel, PHYSICAL_COLOR, alignToPeriod(physicals, months)));
  } else {
    notes.push(isMegaProject(project) ? NO_PREVISTO_TEXT : NO_PHYSICAL_TEXT);
  }
  if (initialAvailable) {
    datasets.push(lineDataset(
      origin ? `Desembolso inicial (${origin.label})` : "Desembolso inicial",
      INITIAL_COLOR,
      alignToPeriod(initial, months),
    ));
  } else {
    notes.push(NO_INITIAL_TEXT);
  }
  return { datasets, notes };
};

const AmpliCurveChart = ({
  project,
  selectedMonths = null,
  onToggleMonth = null,
  onClearMonths = null,
}) => {
  const { filterDates, projectionScopeGeneration } = useProjectContext();
  const { loadUnits } = useProjectActions();
  const workId = project?._id;
  const ampli = isAmpliDisbursementPolicy(project?.cashFlowPlanning);

  // As unidades são a fonte da barra: pedidas aqui (e não só pela tabela da EAP, que nem
  // sempre está montada) e de novo a cada troca de período, que é o que mantém a barra
  // igual à raiz da EAP numa janela diferente da da seleção da obra.
  // E de novo quando a obra é recarregada (depois de salvar uma regra): o projeto novo
  // chega sem as unidades, e a barra ficaria zerada até a próxima troca de período.
  useEffect(() => {
    if (workId && ampli) loadUnits?.();
  }, [workId, ampli, filterDates, projectionScopeGeneration]);

  const months = useMemo(
    () => monthsBetween(filterDates?.beginAt, filterDates?.endAt),
    [filterDates?.beginAt, filterDates?.endAt],
  );
  const series = useMemo(
    () => seriesForPeriod(project, months),
    [project, months],
  );
  // As comparações seguem os rótulos da resultante: sem período legível, os meses dela.
  const comparison = useMemo(
    () => comparisonSeries(project, months.length > 0 ? months : series.map((point) => point.date)),
    [project, months, series],
  );

  if (!isAmpliDisbursementPolicy(project?.cashFlowPlanning)) return null;

  const curve = ampliCurveState(project);
  const detail = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_DETAILS[curve.status] ?? null;
  const hasSeries = series.length > 0;

  const labels = series.map((point) => point.date);
  const datasets = [
    {
      label: RESULTING_LABEL,
      data: series.map((point) => Number(point.value) || 0),
      backgroundColor: RESULTING_COLOR,
      selectable: true,
      order: 1,
    },
    ...comparison.datasets,
  ];

  return (
    <section className={`ampli-curve-chart ampli-curve-chart--${curve.status}`}>
      <h2>Desembolso Ampli</h2>
      <p className="card-label">{ampliCurveStateLabel(curve.status)}</p>
      {detail ? <p>{detail}</p> : null}
      {hasSeries ? (
        <MonthlyBarChart
          labels={labels}
          datasets={datasets}
          showLegend={datasets.length > 1}
          ariaLabel="Gráfico do desembolso mensal da obra"
          selectedMonths={selectedMonths}
          onToggleMonth={onToggleMonth}
          onClearMonths={onClearMonths}
        />
      ) : (
        <p className="empty-state">
          Nenhuma curva de desembolso para desenhar.
        </p>
      )}
      {hasSeries && comparison.notes.length > 0 ? (
        <ul className="ampli-curve-chart__notes" data-testid="curve-chart-notes">
          {comparison.notes.map((note) => <li key={note}>{note}</li>)}
        </ul>
      ) : null}
    </section>
  );
};

export default AmpliCurveChart;
